import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, CalendarDays, MapPin, Clock,
  Users, Tag, ArrowRight, CheckCircle2, Loader
} from 'lucide-react';
import { fetchAgenda, getImageUrl, formatTanggalIndonesia } from '../utils/api';

const KATEGORI_MAP = {
  seminar_workshop: { 
    label: 'Seminar & Workshop', 
    color: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200/50',
    dot: 'bg-indigo-500'
  },
  yudisium_wisuda: { 
    label: 'Yudisium & Wisuda', 
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200/50',
    dot: 'bg-purple-500'
  },
  pengabdian: { 
    label: 'Pengabdian', 
    color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-200/50',
    dot: 'bg-orange-500' 
  },
  umum: { 
    label: 'Umum', 
    color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200/50',
    dot: 'bg-emerald-500'
  },
  akademik: { 
    label: 'Akademik', 
    color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200/50',
    dot: 'bg-rose-500'
  }
};

const CATEGORY_FILTERS = ['Semua', ...Object.values(KATEGORI_MAP).map(k => k.label)];

const formatAgendaWaktu = (mulai, selesai) => {
  if (!mulai) return '-';

  const getT = (val) => new Date(val).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':');

  const d1 = formatTanggalIndonesia(mulai);
  const t1 = getT(mulai);

  if (!selesai) return `${d1}, ${t1} WIB`;

  const d2 = formatTanggalIndonesia(selesai);
  const t2 = getT(selesai);

  if (d1 === d2) {
    return `${d1}, ${t1} - ${t2} WIB`;
  }
  return `${d1} ${t1} WIB s/d ${d2} ${t2} WIB`;
};

const EventCard = ({ ev }) => {
  const isPast = ev._computedStatus === 'past';
  const imageUrl = ev.poster 
    ? getImageUrl(ev.poster, { width: 600, quality: 85, format: 'webp' }) 
    : 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?w=600&q=80';
    
  const timeStr = formatAgendaWaktu(ev.tanggal_mulai, ev.tanggal_selesai);
  const dateStr = formatTanggalIndonesia(ev.tanggal_mulai);
  const catInfo = KATEGORI_MAP[ev.kategori] || { label: ev.kategori || 'Kegiatan', color: 'bg-gray-500/10 text-gray-600 border-gray-200/50', dot: 'bg-gray-400' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`group relative bg-white dark:bg-[#1A182E] rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800/60 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_40px_-12px_rgba(123,47,190,0.15)] transition-all duration-500 ${isPast ? 'grayscale-[0.4] opacity-90' : ''}`}
    >
      {/* Image Section */}
      <div className="relative h-56 overflow-hidden">
        <img 
          src={imageUrl} 
          alt={ev.judul} 
          loading="lazy" 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
        
        {/* Status Badges */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          <div className="flex flex-col gap-2">
            <span className={`backdrop-blur-md px-3 py-1.5 rounded-2xl text-[10px] font-bold uppercase tracking-wider border flex items-center gap-2 shadow-sm ${catInfo.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${catInfo.dot} animate-pulse`} />
              {catInfo.label}
            </span>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            {ev.isOngoing && (
              <span className="backdrop-blur-md bg-blue-500/90 text-white px-3 py-1.2 rounded-full text-[10px] font-bold flex items-center gap-1.5 shadow-lg border border-blue-400/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                LIVE NOW
              </span>
            )}
            {!isPast && !ev.isOngoing && (
              <span className="backdrop-blur-md bg-emerald-500/90 text-white px-3 py-1.2 rounded-full text-[10px] font-bold shadow-lg border border-emerald-400/30">
                COMING SOON
              </span>
            )}
            {isPast && (
              <span className="backdrop-blur-md bg-gray-600/90 text-white px-3 py-1.2 rounded-full text-[10px] font-bold shadow-lg border border-gray-500/30">
                PASSED
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6 relative">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-[#1A1A2E] dark:text-white font-heading leading-tight group-hover:text-[#7B2FBE] transition-colors duration-300 line-clamp-2 min-h-[3.5rem]" title={ev.judul}>
            {ev.judul}
          </h3>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-2xl border border-transparent group-hover:border-[#7B2FBE]/10 transition-colors">
            <div className="w-8 h-8 rounded-xl bg-white dark:bg-[#201E38] shadow-sm flex items-center justify-center text-[#7B2FBE]">
              <CalendarDays size={16} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Tanggal</p>
              <p className="font-semibold text-gray-700 dark:text-gray-200 tracking-tight">{dateStr}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
             <div className="w-8 h-8 flex items-center justify-center text-[#7B2FBE]/70">
              <Clock size={16} />
            </div>
            <p className="text-xs leading-relaxed font-medium">{timeStr}</p>
          </div>

          <div className="flex items-start gap-3 text-sm text-gray-500 dark:text-gray-400 text-xs">
            <div className="w-8 h-8 flex items-center justify-center text-[#7B2FBE]/70">
              <MapPin size={16} />
            </div>
            <p className="leading-relaxed font-medium flex-1">{ev.lokasi || '-'}</p>
          </div>
        </div>

        {ev.peserta && (
          <div className="flex items-center gap-2 mb-6 px-4 py-2 bg-[#F8F7FF] dark:bg-[#201E38] rounded-2xl border border-purple-100 dark:border-purple-900/20">
            <Users size={14} className="text-[#7B2FBE]" />
            <span className="text-xs font-bold text-[#7B2FBE] dark:text-purple-400">Status Peserta: <span className="text-gray-600 dark:text-gray-300 font-medium">{ev.peserta}</span></span>
          </div>
        )}

        <div className="flex items-center gap-3">
          {!isPast ? (
            ev.action_tombol ? (
              <a
                href={ev.action_tombol}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-[#7B2FBE] to-[#9D50BB] text-white font-bold text-xs shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                IKUTI KEGIATAN <ArrowRight size={14} />
              </a>
            ) : (
              <Link
                to="/kontak"
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-[#7B2FBE] to-[#9D50BB] text-white font-bold text-xs shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                IKUTI KEGIATAN <ArrowRight size={14} />
              </Link>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-xs font-bold">
              <CheckCircle2 size={14} className="text-emerald-500" /> KEGIATAN BERAKHIR
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Agenda = () => {
  const [activeFilter, setActiveFilter] = useState('Semua');
  const [dataCMS, setDataCMS] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgenda(-1).then(data => {
      setDataCMS(data);
      setLoading(false);
    });
  }, []);

  const currentDate = new Date();
  
  const processedData = dataCMS.map(ev => {
    const startDate = ev.tanggal_mulai ? new Date(ev.tanggal_mulai) : null;
    const endDate = ev.tanggal_selesai ? new Date(ev.tanggal_selesai) : startDate;
    const isPast = endDate && endDate < currentDate;
    const isOngoing = startDate && startDate <= currentDate && !isPast;
    
    return {
      ...ev,
      isOngoing,
      _computedStatus: isPast ? 'past' : 'upcoming'
    };
  });

  const filtered = processedData.filter(ev => {
    if (activeFilter === 'Semua') return true;
    const catInfo = KATEGORI_MAP[ev.kategori] || { label: ev.kategori };
    return catInfo.label === activeFilter;
  });

  const upcoming = filtered.filter(e => e._computedStatus === 'upcoming');
  const past = filtered.filter(e => e._computedStatus === 'past');

  return (
    <div data-testid="agenda-page">
      <div className="page-hero pt-[70px] relative overflow-hidden">
        {/* Animated Background Sparks */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-xs mb-6 flex-wrap font-bold uppercase tracking-widest">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1.5"><Home size={14} /> BERANDA</Link>
              <div className="w-1 h-1 rounded-full bg-white/30" />
              <span className="text-white">AGENDA & EVENT</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-black text-white font-heading mb-6 tracking-tight">
              Agenda <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-white">&</span> Event
            </h1>
            <p className="text-white/80 max-w-2xl text-lg leading-relaxed font-medium border-l-4 border-purple-400 pl-6">
              Ikuti berbagai rangkaian kegiatan menarik mulai dari seminar internasional, workshop pengembangan skill, hingga momen perayaan akademik di STIKES HI Jambi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#FAF9FF] dark:bg-[#100E1C] relative">
        {/* Subtle geometric pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" 
             style={{ backgroundImage: `radial-gradient(#7B2FBE 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24 relative z-10">
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            {/* Label */}
            <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-3 px-1">
              <Tag size={12} /> Kategori Kegiatan
            </div>

            {/* Scroll strip — scrollable on mobile, wraps on md+ */}
            <div className="relative">
              {/* Right fade hint for mobile */}
              <div className="absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-[#FAF9FF] dark:from-[#08080C] to-transparent pointer-events-none z-10 md:hidden" />

              <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-visible pb-1 md:pb-0 scrollbar-none snap-x snap-mandatory">
                {CATEGORY_FILTERS.map(f => (
                  <button
                    key={f}
                    data-testid={`agenda-filter-${f.toLowerCase()}`}
                    onClick={() => setActiveFilter(f)}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    className={`snap-start shrink-0 whitespace-nowrap px-5 py-2.5 rounded-2xl text-xs font-bold transition-all duration-300 active:scale-95 ${
                      activeFilter === f
                        ? 'bg-[#7B2FBE] text-white shadow-[0_8px_16px_-4px_rgba(123,47,190,0.45)]'
                        : 'bg-white dark:bg-[#1A182E] text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-800 hover:border-purple-200 dark:hover:border-purple-900 shadow-sm'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {loading ? (
            <div className="flex py-32 flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-100 dark:border-purple-900/30 rounded-full animate-spin border-t-[#7B2FBE]" />
                <div className="absolute inset-0 flex items-center justify-center">
                   <div className="w-2 h-2 bg-[#7B2FBE] rounded-full animate-ping" />
                </div>
              </div>
              <p className="mt-6 text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Sinkronisasi Data...</p>
            </div>
          ) : (
            <div className="space-y-20">
              {upcoming.length > 0 && (
                <section>
                  <div className="flex items-end justify-between mb-8 border-b border-gray-100 dark:border-gray-800 pb-4">
                    <h2 className="text-2xl font-black text-[#1A1A2E] dark:text-white font-heading flex items-center gap-3">
                      <span className="flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                      </span>
                      AKAN DATANG
                    </h2>
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-lg">
                      {upcoming.length} AGENDA
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {upcoming.map(ev => <EventCard key={ev.id} ev={ev} />)}
                  </div>
                </section>
              )}

              {past.length > 0 && (
                <section>
                  <div className="flex items-end justify-between mb-8 border-b border-gray-100 dark:border-gray-800 pb-4">
                    <h2 className="text-2xl font-black text-gray-400 dark:text-gray-500 font-heading flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-700" />
                      KEGIATAN LALU
                    </h2>
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-lg">
                      {past.length} RIWAYAT
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {past.map(ev => <EventCard key={ev.id} ev={ev} />)}
                  </div>
                </section>
              )}

              {!loading && upcoming.length === 0 && past.length === 0 && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="col-span-full py-24 text-center bg-white dark:bg-[#1A182E] rounded-[40px] border-2 border-dashed border-gray-100 dark:border-gray-800 shadow-sm"
                >
                  <div className="w-24 h-24 bg-[#F8F7FF] dark:bg-[#201E38] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CalendarDays size={40} className="text-gray-300 dark:text-gray-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-400 dark:text-gray-500 mb-2">Belum Ada Agenda</h3>
                  <p className="text-gray-400 dark:text-gray-600 max-w-xs mx-auto text-sm">Tidak ada jadwal kegiatan yang ditemukan untuk kategori ini.</p>
                </motion.div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Agenda;
